import { Box3, Matrix4, Quaternion, Vector3 } from 'three/webgpu'
import { easing } from 'maath'
import localBounds from './localBounds'

export default class ClawPrize {
	static defaults = {
		grip: 3.8,
		reach: 2,
		snap: 0.1,
		damping: 0.4,
		friction: 0.3,
		speed: 50,
		spin: 10,
		toss: 1,
	}

	constructor(device, body, model, RAPIER, world, depth) {
		this.options = ClawPrize.defaults
		this.device = device
		this.body = body
		this.model = model
		this.types = RAPIER.RigidBodyType
		this.hold = device.position.clone()
		this.hold.y += this.options.grip
		this.grip = new Vector3()
		this.matrix = new Matrix4()
		this.position = new Vector3()
		this.previous = new Vector3()
		this.velocity = new Vector3()
		this.quaternion = new Quaternion()
		this.scale = new Vector3()
		this.axis = new Vector3()
		this.held = false

		model.updateMatrixWorld(true)
		model.attach(device)
		device.position.copy(this.gripPoint())
		device.position.y -= depth
		this.#body(RAPIER, world)
	}

	#body(RAPIER, world) {
		const { damping, friction } = this.options
		const bounds = localBounds(this.device, new Box3())
		const size = bounds.getSize(new Vector3()).multiply(this.device.scale)
		const centre = bounds.getCenter(new Vector3()).multiply(this.device.scale)
		const { position, quaternion } = this.device

		this.width = size.x
		this.height = size.y
		this.depth = position.z
		this.rigid = world.createRigidBody(
			RAPIER.RigidBodyDesc.dynamic()
				.setTranslation(position.x, position.y, position.z)
				.setRotation(quaternion)
				.setLinearDamping(damping)
				.setAngularDamping(damping)
				.enabledRotations(false, false, true)
				.setCcdEnabled(true)
		)

		world.createCollider(
			RAPIER.ColliderDesc.cuboid(size.x / 2, size.y / 2, size.z / 2)
				.setTranslation(centre.x, centre.y, centre.z)
				.setFriction(friction)
				.setFrictionCombineRule(RAPIER.CoefficientCombineRule.Max)
				.setRestitution(0),
			this.rigid
		)
	}

	move(x, y, angle) {
		const { position, quaternion } = this.device

		position.x += x
		position.y += y
		quaternion.premultiply(this.quaternion.setFromAxisAngle(this.axis.set(0, 0, 1), angle))
		this.rigid.setTranslation({ x: position.x, y: position.y, z: position.z }, true)
		this.rigid.setRotation(quaternion, true)
	}

	get x() {
		return this.device.position.x
	}

	get y() {
		return this.device.position.y
	}

	gripPoint() {
		return this.model.worldToLocal(this.body.localToWorld(this.grip.copy(this.hold)))
	}

	get distance() {
		return this.gripPoint().distanceTo(this.device.position)
	}

	#pose() {
		this.device.updateWorldMatrix(true, false)
		this.matrix.copy(this.model.matrixWorld).invert().multiply(this.device.matrixWorld)
		this.matrix.decompose(this.position, this.quaternion, this.scale)
	}

	#catch() {
		if (this.distance > this.options.reach) return

		this.body.attach(this.device)
		this.rigid.setBodyType(this.types.KinematicPositionBased, true)
		this.#pose()
		this.previous.copy(this.position)
		this.velocity.set(0, 0, 0)
		this.held = true
	}

	#release() {
		this.model.attach(this.device)
		this.rigid.setBodyType(this.types.Dynamic, true)
		this.rigid.setLinvel(this.velocity.multiplyScalar(this.options.toss), true)
		this.rigid.setAngvel({ x: 0, y: 0, z: 0 }, true)
		this.held = false
	}

	#follow(delta) {
		this.#pose()

		if (delta > 0) {
			this.velocity
				.subVectors(this.position, this.previous)
				.divideScalar(delta)
				.setZ(0)
				.clampLength(0, this.options.speed)
		}

		this.previous.copy(this.position)
		this.rigid.setNextKinematicTranslation(this.position)
		this.rigid.setNextKinematicRotation(this.quaternion)
	}

	update(delta, grab) {
		if (grab && !this.held) this.#catch()
		if (!grab && this.held) this.#release()
		if (!this.held) return

		easing.damp(this.device.position, 'x', this.hold.x, this.options.snap, delta)
		easing.damp(this.device.position, 'z', this.hold.z, this.options.snap, delta)
		this.#follow(delta)
	}

	#limit() {
		const { speed, spin } = this.options
		const velocity = this.rigid.linvel()
		const turn = this.rigid.angvel()
		const pace = Math.hypot(velocity.x, velocity.y)
		const scale = pace > speed ? speed / pace : 1
		const position = this.rigid.translation()

		this.rigid.setLinvel({ x: velocity.x * scale, y: velocity.y * scale, z: 0 }, false)
		this.rigid.setTranslation({ x: position.x, y: position.y, z: this.depth }, false)
		if (Math.abs(turn.z) > spin) this.rigid.setAngvel({ x: 0, y: 0, z: Math.sign(turn.z) * spin }, false)
	}

	sync() {
		if (this.held) return

		this.#limit()

		const translation = this.rigid.translation()
		const rotation = this.rigid.rotation()

		this.device.position.set(translation.x, translation.y, translation.z)
		this.device.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w)
	}
}
